import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { Seo } from '@/components/seo/Seo'
import { BookingButton } from '@/components/booking/BookingButton'
import { Container } from '@/components/ui/Container'
import { Section } from '@/components/ui/Section'
import { SectionSeparator } from '@/components/ui/SectionSeparator'
import { FadeIn } from '@/components/ui/FadeIn'
import { DecoratedHeader } from '@/components/ui/DecoratedHeader'
import { site } from '@/content/site'
import { testimonials, testimonialsDisclaimer } from '@/content/testimonials'
import { galleryPreviewItems, type GalleryItem } from '@/content/gallery'
import { smoothEase, staggerContainer, staggerItem } from '@/lib/motion'
import { cn } from '@/lib/utils'
import { HomeHeroArtwork } from '@/components/home/HomeHeroArtwork'
import { HeroCollageTilt } from '@/components/home/HeroCollageTilt'
import { HeroMotionCollage } from '@/components/home/HeroMotionCollage'
import { HomeSectionAmbience } from '@/components/home/HomeSectionAmbience'
import { GoogleReviewCard } from '@/components/reviews/GoogleReviewCard'
import { HomeBrandStatement } from '@/components/home/HomeBrandStatement'
import { HomeServicePreview } from '@/components/home/HomeServicePreview'
import { HomeInteriorShowcase } from '@/components/home/HomeInteriorShowcase'
import { HomeSocialGallery } from '@/components/home/HomeSocialGallery'
import { HomeWhySection } from '@/components/home/HomeWhySection'
import { nailsInMotionVideos } from '@/content/nailsInMotionVideos'
import { GalleryPhotoLightbox } from '@/components/gallery/GalleryPhotoLightbox'

export default function Home() {
  const reduced = useReducedMotion()
  const [previewLightbox, setPreviewLightbox] = useState<GalleryItem | null>(null)

  return (
    <>
      <Seo
        title={`${site.name} | Luxury nail salon in ${site.city}`}
        description={`Book manicures and pedicures in ${site.city}, ${site.region}. Quiet studio, clean tools, careful work.`}
        path="/"
        keywords={site.seoKeywords}
      />

      {/* Hero — artwork, collage + copy */}
      <section className="relative min-h-[min(100svh,920px)] overflow-hidden bg-canvas">
        <HomeHeroArtwork />
        <Container className="relative z-10 grid gap-12 py-12 md:min-h-0 md:grid-cols-2 md:items-center md:gap-16 md:py-[var(--spacing-section)] lg:gap-20">
          <div className="max-w-xl">
            <motion.div
              initial={reduced ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: reduced ? 0 : 0.42, ease: smoothEase }}
            >
              <h1 className="font-display text-[clamp(2.1rem,5vw,3rem)] font-semibold leading-[1.08] tracking-tight text-ink">
                {site.heroHeadline}
              </h1>
              <p className="mt-5 max-w-md text-base leading-relaxed text-body">{site.heroAccent}</p>
            </motion.div>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <BookingButton />
            </div>
          </div>

          <motion.div
            className="relative md:min-h-0"
            initial={reduced ? false : { opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: reduced ? 0 : 0.5, delay: reduced ? 0 : 0.08, ease: smoothEase }}
          >
            <HeroCollageTilt>
              <div className="relative [transform-style:preserve-3d]">
                <div
                  aria-hidden
                  className="pointer-events-none absolute -inset-3 -z-10 rounded-[2rem] bg-gradient-to-br from-primary/20 via-transparent to-sage-wash/50 blur-2xl md:-inset-4"
                />
                <div style={{ transform: 'translateZ(12px)' }}>
                  <HeroMotionCollage />
                </div>
              </div>
            </HeroCollageTilt>
          </motion.div>
        </Container>
      </section>

      <SectionSeparator />
      <HomeBrandStatement />
      <SectionSeparator />
      <HomeServicePreview />
      <SectionSeparator />
      <HomeInteriorShowcase />
      {nailsInMotionVideos.length > 0 && (
        <>
          <SectionSeparator />
          <HomeSocialGallery />
        </>
      )}
      <SectionSeparator />
      <HomeWhySection />
      <SectionSeparator />
      <Section className="relative overflow-hidden">
        <HomeSectionAmbience variant="gallery" />
        <Container className="relative z-10">
          <DecoratedHeader
            size="section"
            eyebrow="Recent"
            eyebrowAccent="Sets"
            title="Gallery preview"
            sparkle={false}
          ></DecoratedHeader>
          {galleryPreviewItems.length > 0 ? (
            <div
              className={cn(
                'grid gap-2.5 sm:gap-3 md:gap-4',
                'grid-cols-3 sm:grid-cols-4 md:grid-cols-5'
              )}
            >
              {galleryPreviewItems.map((p, i) => (
                <motion.div
                  key={p.id}
                  className="min-w-0"
                  initial={reduced ? false : { opacity: 0, scale: 0.96 }}
                  whileInView={reduced ? undefined : { opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{
                    duration: 0.5,
                    delay: reduced ? 0 : Math.min(i * 0.05, 0.3),
                    ease: smoothEase,
                  }}
                >
                  <motion.button
                    type="button"
                    onClick={() => setPreviewLightbox(p)}
                    aria-label={`Enlarge: ${p.alt}`}
                    className={cn(
                      'relative block aspect-[3/4] w-full cursor-pointer overflow-hidden rounded-[var(--radius-button)]',
                      'border-0 bg-transparent p-0 text-left text-ink',
                      'transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-[var(--shadow-card-hover)]',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink',
                      'motion-reduce:hover:translate-y-0'
                    )}
                  >
                    <div className="absolute inset-0 overflow-hidden">
                      <img
                        src={p.src}
                        alt={p.alt}
                        width={p.width}
                        height={p.height}
                        loading="lazy"
                        decoding="async"
                        sizes="(max-width: 639px) 33vw, (max-width: 767px) 25vw, (max-width: 1023px) 20vw, (max-width: 1279px) 17vw, 14vw"
                        className="h-full w-full object-cover [backface-visibility:hidden]"
                      />
                    </div>
                  </motion.button>
                </motion.div>
              ))}
            </div>
          ) : (
            <p className="rounded-[var(--radius-button)] border border-dashed border-hairline bg-canvas px-6 py-10 text-center text-body">
              Gallery photos coming soon.
            </p>
          )}
          <Link to="/gallery" className="link-modern mt-4 inline-block">
              See full gallery
            </Link>
          <FadeIn className="mt-8" delay={0.04}>
            <BookingButton />
          </FadeIn>
        </Container>
      </Section>

      <GalleryPhotoLightbox
        item={previewLightbox}
        onClose={() => setPreviewLightbox(null)}
        reduced={!!reduced}
      />
      <SectionSeparator />
      <Section id="reviews" className="relative scroll-mt-24 overflow-hidden bg-canvas pb-10 md:pb-12">
        <HomeSectionAmbience variant="reviews" />
        <Container className="relative z-10">
          <DecoratedHeader
            size="section"
            eyebrow={testimonialsDisclaimer}
            hideEyebrowRule
            title="Loved by our clients"
            description="Notes from people who have sat in our chairs. We read every one and appreciate the time."
            sparkle={false}
          />
          <motion.div
            className="mt-10 grid gap-6 md:grid-cols-3"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
          >
            {testimonials.map((t) => (
              <motion.div key={t.id} variants={staggerItem} className="h-full">
                <GoogleReviewCard testimonial={t} className="h-full" />
              </motion.div>
            ))}
          </motion.div>
          <FadeIn className="mt-8" delay={0.06}>
            <BookingButton />
          </FadeIn>
        </Container>
      </Section>
    </>
  )
}
